import { useSystemMetrics } from "@/hooks/useEventManagement";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { 
  Cpu, 
  HardDrive, 
  MemoryStick, 
  Wifi, 
  Activity,
  TrendingUp,
  TrendingDown,
  AlertTriangle
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const metricIcons = {
  'Server CPU Usage': Cpu,
  'Memory Usage': MemoryStick,
  'Disk Usage': HardDrive,
  'Network Latency': Wifi,
  'Queue Processing Rate': Activity
};

const getMetricStatus = (value: number, warning: number, critical: number, isLatency = false) => {
  if (isLatency) {
    // For latency, higher is worse
    if (value >= critical) return { status: 'critical', color: 'text-red-500', variant: 'destructive' as const };
    if (value >= warning) return { status: 'warning', color: 'text-yellow-500', variant: 'secondary' as const };
    return { status: 'healthy', color: 'text-green-500', variant: 'default' as const };
  } else {
    // For usage metrics, higher is worse
    if (value >= critical) return { status: 'critical', color: 'text-red-500', variant: 'destructive' as const };
    if (value >= warning) return { status: 'warning', color: 'text-yellow-500', variant: 'secondary' as const };
    return { status: 'healthy', color: 'text-green-500', variant: 'default' as const };
  }
};

const getProgressValue = (value: number, warning: number, critical: number, isLatency = false) => {
  if (isLatency) {
    // For latency, show progress relative to critical threshold
    return Math.min((value / critical) * 100, 100);
  } else {
    // For usage metrics, show actual percentage
    return Math.min(value, 100);
  }
};

export function SystemMetricsPanel() {
  const { data: metricsData, isLoading } = useSystemMetrics();

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Loading System Metrics...</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-32">
            <Activity className="h-6 w-6 animate-spin" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Group metrics by name and get the latest value for each
  const latestMetrics = metricsData?.reduce((acc, metric) => {
    if (!acc[metric.metric_name] || new Date(metric.recorded_at) > new Date(acc[metric.metric_name].recorded_at)) {
      acc[metric.metric_name] = metric;
    }
    return acc;
  }, {} as Record<string, typeof metricsData[0]>);

  const metrics = Object.values(latestMetrics || {});

  // Calculate overall system status
  const criticalMetrics = metrics.filter(m => {
    const isLatency = m.metric_name.toLowerCase().includes('latency');
    return isLatency ? m.metric_value >= m.threshold_critical : m.metric_value >= m.threshold_critical;
  });

  const warningMetrics = metrics.filter(m => {
    const isLatency = m.metric_name.toLowerCase().includes('latency');
    if (isLatency) {
      return m.metric_value >= m.threshold_warning && m.metric_value < m.threshold_critical;
    } else {
      return m.metric_value >= m.threshold_warning && m.metric_value < m.threshold_critical;
    }
  });

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">System Status</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {criticalMetrics.length === 0 && warningMetrics.length === 0 ? (
                <span className="text-green-500">Optimal</span>
              ) : criticalMetrics.length > 0 ? (
                <span className="text-red-500">Critical</span>
              ) : (
                <span className="text-yellow-500">Warning</span>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {metrics.length} metrics monitored
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Critical Alerts</CardTitle>
            <AlertTriangle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-500">
              {criticalMetrics.length}
            </div>
            <p className="text-xs text-muted-foreground">
              Metrics requiring immediate attention
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Performance</CardTitle>
            <TrendingUp className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {((metrics.length - criticalMetrics.length - warningMetrics.length) / metrics.length * 100).toFixed(0)}%
            </div>
            <p className="text-xs text-muted-foreground">
              Metrics within normal range
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Metrics */}
      <Card>
        <CardHeader>
          <CardTitle>System Metrics</CardTitle>
          <CardDescription>
            Real-time performance metrics and resource utilization
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 md:grid-cols-2">
            {metrics.map((metric) => {
              const IconComponent = metricIcons[metric.metric_name as keyof typeof metricIcons] || Activity;
              const isLatency = metric.metric_name.toLowerCase().includes('latency');
              const status = getMetricStatus(metric.metric_value, metric.threshold_warning, metric.threshold_critical, isLatency);
              const progressValue = getProgressValue(metric.metric_value, metric.threshold_warning, metric.threshold_critical, isLatency);

              return (
                <div key={metric.id} className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <IconComponent className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{metric.metric_name}</span>
                    </div>
                    <Badge variant={status.variant}>
                      {status.status}
                    </Badge>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className={`font-bold ${status.color}`}>
                        {metric.metric_value.toFixed(1)} {metric.metric_unit}
                      </span>
                      <span className="text-muted-foreground">
                        {formatDistanceToNow(new Date(metric.recorded_at), { addSuffix: true })}
                      </span>
                    </div>

                    <Progress 
                      value={progressValue} 
                      className="h-2"
                    />

                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>Warning: {metric.threshold_warning}{metric.metric_unit}</span>
                      <span>Critical: {metric.threshold_critical}{metric.metric_unit}</span>
                    </div>

                    {metric.metadata && Object.keys(metric.metadata).length > 0 && (
                      <div className="text-xs text-muted-foreground">
                        {Object.entries(metric.metadata).map(([key, value]) => (
                          <div key={key} className="flex justify-between">
                            <span>{key}:</span>
                            <span>{String(value)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}